import {chain} from 'lodash';

import {isNotNull} from '../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {DataEventActionsEnum} from '../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {SearchDataEventObject} from '../../../../libs/shared/core/src/models/tracking/objectNames/searchDataEventObject';
import {
  InterfaceEventsEnum,
  trackDataEvent,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {ModifiersEnum} from '../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {
  computeIsNegatedTermPresent,
  computeIsNonNegatedTermPresent,
  isSearchModifierToken,
  splitSearchQuery,
} from '../../../../srcCommon/inboxes/search/searchQueryHelpers';
import {ConversationHighlightModel} from '../../../models/api/conversationHighlightModel';
import {SmartMixClassnamesEnum} from '../../../models/api/smartMixMatchModel';
import {InboxesSearchLocationsEnum} from '../../../models/url/models/inboxesUrlModel';
import {ItemId} from '../cells/content/cellsTypes';

interface SearchTrackingOptionsBase {
  queryHash?: string;
}

interface SearchTrackingOptionsQueries {
  inputQuery: string;
  outputQuery: string;
}

export function trackSearchSessionStart(trackingOptions: SearchTrackingOptionsBase) {
  const {queryHash} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_SESSION_START, {
    query_hash: queryHash,
  });
}

export function trackSearchSessionEnd(trackingOptions: SearchTrackingOptionsBase) {
  const {queryHash} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_SESSION_END, {
    query_hash: queryHash,
  });
}

export interface SearchQueryTrackingOptions extends SearchTrackingOptionsBase {
  queryText: string;
  location: InboxesSearchLocationsEnum;
  addedModifiers: string | null;
  isPasted?: boolean;
  isFolderSwitch?: boolean;
  isAppliedQuerySuggestion: boolean;
}

export function trackSearchQuery(trackingOptions: SearchQueryTrackingOptions) {
  const {
    queryText,
    queryHash,
    location,
    addedModifiers,
    isPasted = false,
    isFolderSwitch = false,
    isAppliedQuerySuggestion,
  } = trackingOptions;

  const parts = splitSearchQuery(queryText);

  const tokenCount = chain(parts)
    .map(({token}) => {
      if (!token) {
        return 0;
      }

      if (isSearchModifierToken(token)) {
        return 1;
      }

      if (!token.value) {
        return 0;
      }

      // token.value can contain spaces if it is a phrase token (double-quoted).
      return token.value.split(/\s+/).length;
    })
    .sum()
    .value();

  const usedModifiers = chain(parts)
    // Only consider modifier tokens with a value.
    .map(({token}) => token && isSearchModifierToken(token) && token.value && token.modifier)
    .compact()
    .uniq()
    .value();

  const hasNegatedTerm = computeIsNegatedTermPresent(parts);
  const hasNormalTerm = computeIsNonNegatedTermPresent(parts);

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_QUERY, {
    query_hash: queryHash,
    query: queryText,
    full_query: isNotNull(addedModifiers) ? `${addedModifiers} ${queryText}` : queryText,
    tokens_count: tokenCount,
    used_modifiers: usedModifiers.join(','),
    added_modifiers: addedModifiers?.split(' ').join(','),
    location,
    pasted_search_query: isPasted,
    folder_switch: isFolderSwitch,
    has_negated_term: hasNegatedTerm,
    has_normal_term: hasNormalTerm,
    is_typo_correction: isAppliedQuerySuggestion,
  });
}

export function trackSearchClearQuery(trackingOptions: SearchTrackingOptionsBase) {
  const {queryHash} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_CLEAR_QUERY, {
    query_hash: queryHash,
  });
}

interface SearchDropdownClickTrackingOptions extends SearchTrackingOptionsBase, SearchTrackingOptionsQueries {
  isRecentQuery: boolean;
  isModifierSuggestion: boolean;
  modifierSuggestionType: ModifiersEnum | null;

  isConversationSuggestion: boolean;
  topResultsConversationId: number | null;
  topResultsQuery: string | null;

  conversationClassificationLabels?: ReadonlyArray<string>;
  modifierHelperType: ModifiersEnum | null;
  selectedWithTab: boolean;

  isSmartMixItem: boolean;
  // The following are only defined if isSmartMixItem is true.
  // The suggestion type.
  smartMixType: SmartMixClassnamesEnum | null;
  // The modifier:value string corresponding the clicked suggestion.
  smartMixValue: string | null;
  // The index of the clicked suggestion (top suggestion = 0).
  smartMixIndex: number | null;
  // The suggestion query that produced that suggestion.
  smartMixQuery: string | null;
  // The namespace for which the suggestions were computed.
  smartMixNamespace: string | null;
  // The suggestion's score, not always defined.
  smartMixScore: number | null;
  // The suggestion's resource id, not always defined.
  smartMixResourceId: number | null;
  // Did this click happen while the client was refiltering?
  isClientSideFiltering: boolean | null;
}

export function trackSearchDropdownClick(trackingOptions: SearchDropdownClickTrackingOptions) {
  const {
    topResultsConversationId,
    topResultsQuery,
    queryHash,
    isRecentQuery,
    isSmartMixItem,
    isModifierSuggestion,
    modifierSuggestionType,
    isConversationSuggestion,
    conversationClassificationLabels,
    modifierHelperType,
    selectedWithTab,
    smartMixType,
    smartMixValue,
    smartMixIndex,
    smartMixQuery,
    smartMixNamespace,
    smartMixScore,
    smartMixResourceId,
    inputQuery,
    outputQuery,
    isClientSideFiltering,
  } = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_DROPDOWN_CLICK, {
    query_hash: queryHash,
    is_recent_query: isRecentQuery,
    is_modifier_suggestion: isModifierSuggestion,
    modifier_suggestion_type: modifierSuggestionType,
    is_conversation_suggestion: isConversationSuggestion,
    top_results_conversation_id: topResultsConversationId,
    top_results_query: topResultsQuery,
    conversation_classification_labels: conversationClassificationLabels,
    modifier_helper_type: modifierHelperType,
    selected_with_tab: selectedWithTab,
    is_smart_mix: isSmartMixItem,
    smart_mix_type: smartMixType,
    smart_mix_value: smartMixValue,
    smart_mix_index: smartMixIndex,
    smart_mix_query: smartMixQuery,
    smart_mix_namespace: smartMixNamespace,
    smart_mix_score: smartMixScore,
    smart_mix_resource_id: smartMixResourceId,
    is_client_side_filtering: isClientSideFiltering,
    input_query: inputQuery,
    output_query: outputQuery,
  });
}

interface SearchDropdownExpandTrackingOptions extends SearchTrackingOptionsBase {
  expand: boolean;
}

export function trackSearchDropdownExpand(trackingOptions: SearchDropdownExpandTrackingOptions) {
  const {queryHash, expand} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_DROPDOWN_EXPAND, {
    query_hash: queryHash,
    expand,
  });
}

export interface SearchHintClickTrackingOptions
  extends SearchTrackingOptionsBase,
    SearchTrackingOptionsQueries {
  type: string;
  shouldEnrichQuery?: boolean;
  isHasNoCalendar: boolean;
  modifier?: ModifiersEnum | 'before_after';
}

export function trackSearchHintClick(trackingOptions: SearchHintClickTrackingOptions) {
  const {type, shouldEnrichQuery, queryHash, isHasNoCalendar, inputQuery, outputQuery, modifier} =
    trackingOptions;

  // Track hint click.
  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_HINT_CLICK, {
    type,
    enrich_query: shouldEnrichQuery,
    query_hash: queryHash,
    is_has_no_calendar: isHasNoCalendar,
    input_query: inputQuery,
    output_query: outputQuery,
    modifier,
  });
}

export interface SearchHintDropdownOpenTrackingOptions extends SearchTrackingOptionsBase {
  modifier: ModifiersEnum | 'date';
}

export function trackSearchHintDropdownOpen(trackingOptions: SearchHintDropdownOpenTrackingOptions) {
  const {queryHash, modifier} = trackingOptions;

  // Track hint click.
  trackDataEvent(
    {
      name: 'search',
      payload: {},
    },
    DataEventActionsEnum.EXPAND,
    SearchDataEventObject.HINT_DROPDOWN,
    {
      query_hash: queryHash,
      modifier,
    },
  );
}

export function trackSearchHintMoreFiltersClick(trackingOptions: SearchTrackingOptionsBase) {
  const {queryHash} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_HINT_MORE_FILTERS_CLICK, {
    query_hash: queryHash,
  });
}

export function trackSearchQueryBuilderClick(trackingOptions: SearchTrackingOptionsBase) {
  const {queryHash} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_QUERY_BUILDER_CLICK, {
    query_hash: queryHash,
  });
}

export function trackSearchQueryBuilderResetClick(trackingOptions: SearchTrackingOptionsBase) {
  const {queryHash} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_QUERY_BUILDER_RESET_CLICK, {
    query_hash: queryHash,
  });
}

interface SearchQueryBuilderSearchClickTrackingOptions
  extends SearchTrackingOptionsBase,
    SearchTrackingOptionsQueries {
  changedInputs: ReadonlyArray<ModifiersEnum>;
}

export function trackSearchQueryBuilderSearchClick(
  trackingOptions: SearchQueryBuilderSearchClickTrackingOptions,
) {
  const {changedInputs, inputQuery, outputQuery, queryHash} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_QUERY_BUILDER_SEARCH_CLICK, {
    query_hash: queryHash,
    changed_inputs: changedInputs,
    input_query: inputQuery,
    output_query: outputQuery,
  });
}

interface SearchConversationCellClickOptions<T extends ItemId> extends SearchTrackingOptionsBase {
  id: T;
  position: number;
  version?: string;
  matchedQueries: ReadonlyArray<string> | undefined;
  highlights: ConversationHighlightModel | undefined;
  highlightLength: number;
  classificationLabels: ReadonlyArray<string> | undefined;
  isTeamConversation: boolean | undefined;
}

export function trackSearchConversationCellClick<T extends ItemId = ItemId>(
  trackingOptions: SearchConversationCellClickOptions<T>,
) {
  const {
    id,
    position,
    matchedQueries = [],
    highlights,
    highlightLength,
    classificationLabels,
    queryHash,
    isTeamConversation,
  } = trackingOptions;

  const computeHighlightKeyTracking = (key: 'recipient' | 'summary' | 'subject' | 'attachments') => {
    const highlightKey = highlights?.[key];

    if (!highlightKey) {
      return {
        [`${key}_highlights`]: 0,
        [`${key}_acceptable_highlights`]: 0,
      };
    }

    return {
      [`${key}_highlights`]: highlightKey.ranges.length,
      [`${key}_acceptable_highlights`]: highlightKey.ranges.filter((range) => range.start < highlightLength)
        .length,
    };
  };

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_P2_CONVERSATION_CLICK, {
    query_hash: queryHash,
    selected_conversation_id: id,
    position,
    matched_queries: matchedQueries.join(','),
    ...computeHighlightKeyTracking('recipient'),
    ...computeHighlightKeyTracking('summary'),
    ...computeHighlightKeyTracking('subject'),
    ...computeHighlightKeyTracking('attachments'),
    classification_labels: classificationLabels?.join(','),
    is_team_conversation: isTeamConversation,
  });
}

export function trackSearchNoResultsHelpfulTipClick(trackingOptions: SearchTrackingOptionsBase) {
  const {queryHash} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_NO_RESULTS_HELPFUL_TIP_CLICK, {
    query_hash: queryHash,
  });
}

interface SearchNoResultsNudgeTrackingOptions extends SearchTrackingOptionsBase {
  currentLocation: InboxesSearchLocationsEnum;
  suggestedLocation: InboxesSearchLocationsEnum;
}

export function trackSearchNoResultsNudgeClick(trackingOptions: SearchNoResultsNudgeTrackingOptions) {
  const {queryHash, currentLocation, suggestedLocation} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_NO_RESULTS_NUDGE_CLICK, {
    query_hash: queryHash,
    location: currentLocation,
    suggested_location: suggestedLocation,
  });
}

interface SearchClientSideFilteringDiscrepancyTrackingOptions extends SearchTrackingOptionsBase {
  query: string;
  namespace: string | undefined;
  nonMatchingStaticSuggestions: ReadonlyArray<string>;
  nonMatchingContactSuggestions: ReadonlyArray<string>;
  nonMatchingSuggestions: ReadonlyArray<string>;
}

export function trackSearchClientSideFilteringDiscrepancy(
  trackingOptions: SearchClientSideFilteringDiscrepancyTrackingOptions,
) {
  const {
    queryHash,
    query,
    namespace,
    nonMatchingStaticSuggestions,
    nonMatchingContactSuggestions,
    nonMatchingSuggestions,
  } = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_CLIENT_SIDE_FILTERING_DISCREPANCY, {
    query_hash: queryHash,
    query,
    namespace,
    non_matching_static_suggestions: nonMatchingStaticSuggestions,
    non_matching_contact_suggestions: nonMatchingContactSuggestions,
    non_matching_suggestions: nonMatchingSuggestions,
  });
}

interface SearchSuggestionsBulkLatencyTrackingOptions {
  elapsedMs: number;
  contactSuggestionsCount: number;
  suggestionsCount: number;
}

export function trackSearchSuggestionsBulkLatency(
  trackingOptions: SearchSuggestionsBulkLatencyTrackingOptions,
) {
  const {elapsedMs, contactSuggestionsCount, suggestionsCount} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_SUGGESTIONS_BULK_LATENCY, {
    elapsed_ms: elapsedMs,
    contact_suggestions_count: contactSuggestionsCount,
    suggestions_count: suggestionsCount,
  });
}

interface SearchSearchInConversationJumpToMatchOptions {
  query: string;
  jumpDirection: 'next' | 'previous';
  activityId: number | undefined;
}

export function trackSearchInConversationJumpToMatch(
  trackingOptions: SearchSearchInConversationJumpToMatchOptions,
) {
  const {query, jumpDirection, activityId} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.P3_CONVERSATION_SEARCH_CLICK, {
    query,
    jump_direction: jumpDirection,
    activity_id: activityId,
  });
}

interface SearchEmptyStateViewTrackingOptions extends SearchTrackingOptionsBase {
  variant: 'no_results' | 'other_location';
}

export function trackSearchEmptyStateView(trackingOptions: SearchEmptyStateViewTrackingOptions) {
  const {queryHash, variant} = trackingOptions;

  trackDataEvent(
    {name: 'search', payload: {query_hash: queryHash}},
    DataEventActionsEnum.VIEW,
    SearchDataEventObject.EMPTY_STATE,
    {
      variant,
    },
  );
}
